import type {
    CoverableDTO,
    CoverableChangesDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type { ChoDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/cho';

import type { OPCRowInformation } from '../../../config/flavourConfig';
import type { BusinessFlow } from '../../../recoil/types/types';
import getContentAdjustDetailsConfig from './lobAdjustDetails/privateContent';
import getAccidentAdjustDetailsConfig from './lobAdjustDetails/accident';
import getVehicleAdjustDetailsConfig from './lobAdjustDetails/vehicle';
import getTravelAdjustDetailsConfig from './lobAdjustDetails/travel';
import getSingleTripTravelAdjustDetailsConfig from './lobAdjustDetails/singleTripTravel';
import getSingleTripCancellationAdjustDetailsConfig from './lobAdjustDetails/singleTripCancellation';
import getChoAdjustDetailsConfig from './lobAdjustDetails/changeOfOwner';
import getPrhAdjustDetailsConfig from './lobAdjustDetails/privateHouse';

const getCoverableAdjustDetailsConfig = (
    coverable: CoverableDTO,
    getHippoText: (resourceKey: string) => string,
    businessFlow: BusinessFlow,
    coverableChangesDTO: CoverableChangesDTO[]
): OPCRowInformation[] => {
    const isOpc = businessFlow === 'OPC';
    /* New item in below configs should have it own key,
     please check if that new item is existed in configs
     in getImportantInformationCoverableDetailsConfig(CoverableDetailsImportantInformationUtil.tsx)
     If yes then the existed item's key need to be reuse for the new item */
    switch (coverable.lobCode) {
        case 'PET':
            return [];
        case 'PRC':
            return getContentAdjustDetailsConfig(coverable, isOpc, coverableChangesDTO);
        case 'ACC':
            return getAccidentAdjustDetailsConfig(coverable, isOpc, coverableChangesDTO);
        case 'MVP':
            return getVehicleAdjustDetailsConfig(
                coverable,
                getHippoText,
                isOpc,
                coverableChangesDTO
            );
        case 'TRA':
            return getTravelAdjustDetailsConfig(coverable, isOpc, coverableChangesDTO);
        case 'STT':
            return getSingleTripTravelAdjustDetailsConfig(coverable);
        case 'STC':
            return getSingleTripCancellationAdjustDetailsConfig(coverable);
        case 'CHO':
            return getChoAdjustDetailsConfig(coverable as ChoDTO, getHippoText);
        case 'PRH':
            return getPrhAdjustDetailsConfig(coverable, isOpc, coverableChangesDTO);
        default:
            return [];
    }
};

const isCoverableInsuranceSumsEditable = (lobCode: string): boolean => {
    switch (lobCode) {
        case 'PET':
        case 'PRC':
            return false;
        case 'ACC':
        case 'MVP':
        case 'TRA':
        case 'STT':
        case 'STC':
            throw new Error('Under development');
        default:
            throw new Error('Unknown lob');
    }
};

enum AdjustItemKeys {
    ACC_ADULT_EMPLOYMENT,
    ACC_ADULT_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM,
    ACC_ADULT_INJURY_TOOTH_DAMAGE_DEGREE_TERM,
    ACC_ADULT_CONTRACT_TYPE,
    ACC_ADULT_FULLTIME_LEISURE,
    ACC_ADULT_BENEFICIARY,
    ACC_YOUNG_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM,
    ACC_YOUNG_INJURY_TOOTH_DAMAGE_DEGREE_TERM,
    ACC_YOUNG_FULLTIME_LEISURE,
    ACC_CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM,
    ACC_CHILD_FULLTIME_LEISURE,
    ACC_CHILD_INJURY_TOOTH_DAMAGE_DEGREE_TERM,
    PRC_PRIVATE_CONTENT_CONTENT_BAGGAGE_DEDUCTIBLE,
    PRC_PRIVATE_CONTENT_COHOUSING_NUMBER_IN_HOUSEHOLD,
    PRC_PRIVATE_CONTENT_NONE_COHOUSING_NUMBER_IN_HOUSEHOLD,
    PRC_YOUTH_CONTENT_CONTENT_BAGGAGE_DEDUCTIBLE,
    PRC_YOUTH_CONTENT_BICYCLES_DEDUCTIBLE,
    MVP_CAR_YEARLY_MILEAGE,
    TRA_AREA_DESTINATION,
    STC_NUMBER_INSURED_PERSONS,
    STC_AREA_DESTINATION,
    STC_DEPARTURE_DATE,
    STC_TRAVEL_PRICE,
    STT_AREA_DESTINATION,
    STT_DEPARTURE_DATE,
    STT_RETURN_DATE,
    CTP_NUMBER_PERSON_IN_HOUSEHOLD,
    CTP_HOURSE_NUMBER,
    CTP_DOG_NUMBER,
    CTP_HAS_MACHINERY,
    UVP_VALUE_OF_CARAVANS_CONTENT,
    UVP_CARAVAN_IS_STATIONARY
}

export { isCoverableInsuranceSumsEditable, AdjustItemKeys };

export default getCoverableAdjustDetailsConfig;
